/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { sum, map } from 'lodash';
import { StyleSheet, css } from 'aphrodite';

import { BrandColors } from '@blend/enterprise-components';
import { getPercentagesFromWidths } from '../../utils/ledgerUtils';

const styles = StyleSheet.create({
  ledgerExtendedRowItem: {
    padding: '0 4px',
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  ledgerRow: {
    borderBottom: `1px solid ${BrandColors.CLOUD_30}`,
    paddingBottom: 4,
    width: '100%',
  },
  ledgerRowItem: {
    alignItems: 'center',
    display: 'flex',
    padding: '0 4px 0 0',
    textAlign: 'left',
  },
  ledgerSubRow: {
    display: 'flex',
    padding: '8px 25px 4px 0',
  },
});

export namespace LedgerRow {
  export interface Props {
    banner?: React.ReactNode;
    columnWidths: number[];
    columns: React.ReactNode[];
    overwritingAction?: React.ReactNode;
    rowActions?: {
      actions: React.ReactNode[];
      columnWidths: number[];
    };
  }
}

export const LedgerRow: React.FC<LedgerRow.Props> = ({
  columns,
  columnWidths,
  banner,
  overwritingAction,
  rowActions = { actions: [], columnWidths: [] },
}) => {
  const renderLedgerRowCol = (item: React.ReactNode, idx: number, width: string, isExtendedRowCol = false) => {
    const ledgerRowColStyle = isExtendedRowCol ? styles.ledgerExtendedRowItem : styles.ledgerRowItem;
    return (
      <div className={css(ledgerRowColStyle)} data-it="LedgerRow-column" key={idx} style={{ width }}>
        {item}
      </div>
    );
  };

  const renderLedgerRowActions = () => {
    return map(overwritingAction ? [overwritingAction] : rowActions.actions, (item, actionIdx) =>
      renderLedgerRowCol(item, actionIdx, actionWidthPercentage[actionIdx]),
    );
  };

  const totalWidth = sum(columnWidths) + sum(rowActions.columnWidths);
  const colWidthsPercentage = getPercentagesFromWidths(columnWidths, totalWidth);
  const actionWidthPercentage = getPercentagesFromWidths(rowActions.columnWidths, totalWidth);

  return (
    <div className={css(styles.ledgerRow)}>
      {banner}
      <div className={css(styles.ledgerSubRow)} data-it="LedgerSubRow">
        {map(columns, (item, colIdx) => renderLedgerRowCol(item, colIdx, colWidthsPercentage[colIdx]))}
        {renderLedgerRowActions()}
      </div>
    </div>
  );
};
